import { useMutation, useQuery } from "@tanstack/react-query"
import { attackBase, getBaseConfig, getMyBase, scoutBase } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { BaseMapMarker, BaseScoutIntel } from "@/types/base"
import { formatStockFullness } from "@/utils/base"
import { useMemo, useState } from "react"

type Props = {
  marker: BaseMapMarker | null
  onClose: () => void
  onOpenOwnBase: () => void
}

export default function MapBaseDetailSheet({
  marker,
  onClose,
  onOpenOwnBase,
}: Props) {
  const { passport, collectionCards, syncGameState } = useGuardiansPassport()
  const [intel, setIntel] = useState<BaseScoutIntel | null>(null)
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([])

  const mineQuery = useQuery({
    queryKey: ["base-mine"],
    queryFn: getMyBase,
  })

  const configQuery = useQuery({
    queryKey: ["base-config"],
    queryFn: getBaseConfig,
  })

  const scout = useMutation({
    mutationFn: () => scoutBase(marker!.id),
    onSuccess: (data) => setIntel(data),
  })

  const attack = useMutation({
    mutationFn: () =>
      attackBase(marker!.id, {
        attackCardIds: selectedCardIds,
      }),
    onSuccess: async () => {
      await syncGameState()
      onClose()
    },
  })

  const isOwn =
    marker != null &&
    (mineQuery.data?.base?.id === marker.id ||
      marker.userId === passport?.user_id)

  const attackCards = useMemo(
    () => collectionCards.filter((c) => c.id.length > 0).slice(0, 24),
    [collectionCards]
  )

  if (!marker) return null

  const raidMax = configQuery.data?.raid?.cardMax ?? 3

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="max-h-[75dvh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-white/10 bg-guardians-bg p-4"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">{marker.nickname}</h2>
            <p className="text-xs text-guardians-muted">
              {marker.league ?? "—"} · ядро Lv.{marker.coreLevel} · склад{" "}
              {formatStockFullness(marker.stockFullness)}
              {marker.hasShield ? " · щит" : ""}
            </p>
          </div>
          <button type="button" className="text-sm text-guardians-muted" onClick={onClose}>
            ✕
          </button>
        </div>

        {isOwn ? (
          <button
            type="button"
            className="mt-2 w-full rounded-xl bg-guardians-accent py-3 text-sm text-guardians-bg"
            onClick={() => {
              onClose()
              onOpenOwnBase()
            }}
          >
            Моя база
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={scout.isPending}
              className="mt-2 w-full rounded-xl border border-guardians-accent py-2 text-sm text-guardians-accent disabled:opacity-50"
              onClick={() => scout.mutate()}
            >
              {scout.isPending ? "Разведка…" : "Разведать базу"}
            </button>

            {intel ? (
              <p className="mt-2 text-xs text-guardians-muted">
                Защита: {intel.guardTier ?? "—"} · сила {intel.guardPower ?? "?"}
              </p>
            ) : null}

            {intel ? (
              <div className="mt-3">
                <p className="mb-1 text-xs text-guardians-muted">
                  Карты для рейда (до {raidMax})
                </p>
                <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
                  {attackCards.map((card) => {
                    const on = selectedCardIds.includes(card.id)
                    return (
                      <li key={card.id}>
                        <button
                          type="button"
                          className={`w-full rounded-lg border px-2 py-1 text-left ${
                            on
                              ? "border-guardians-accent bg-guardians-accent/10"
                              : "border-white/10"
                          }`}
                          onClick={() =>
                            setSelectedCardIds((prev) => {
                              if (prev.includes(card.id)) {
                                return prev.filter((id) => id !== card.id)
                              }
                              if (prev.length >= raidMax) return prev
                              return [...prev, card.id]
                            })
                          }
                        >
                          {card.element} · {card.colorRank} Lv.{card.level}
                        </button>
                      </li>
                    )
                  })}
                </ul>
                <button
                  type="button"
                  disabled={
                    attack.isPending ||
                    selectedCardIds.length === 0 ||
                    selectedCardIds.length > raidMax
                  }
                  className="mt-3 w-full rounded-xl bg-red-600/90 py-3 text-sm text-white disabled:opacity-40"
                  onClick={() => attack.mutate()}
                >
                  {attack.isPending ? "Атака…" : "Атаковать базу"}
                </button>
              </div>
            ) : null}

            {scout.isError ? (
              <p className="mt-2 text-sm text-red-300">
                {getGameApiErrorMessage(scout.error, "Разведка не удалась")}
              </p>
            ) : null}
            {attack.isError ? (
              <p className="mt-2 text-sm text-red-300">
                {getGameApiErrorMessage(attack.error, "Атака не удалась")}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
